/**
 * Defensive Sentry browser SDK initialization.
 *
 * Mirrors backend/app/observability/sentry_setup.py:
 * - initSentry() returns void and NEVER throws.
 * - Missing VITE_SENTRY_DSN = silent no-op.
 * - Any unexpected exception is logged and swallowed.
 *
 * Usage from main.tsx (BEFORE ReactDOM.createRoot().render):
 *     import { initSentry } from '@/observability/sentry'
 *     initSentry()
 */

type SentryState = {
  enabled: boolean
  dsn_configured: boolean
  init_attempted: boolean
  init_error: string | null
}

const state: SentryState = {
  enabled: false,
  dsn_configured: false,
  init_attempted: false,
  init_error: null,
}

function readFloatEnv(key: string, fallback: number): number {
  const raw = (import.meta.env as Record<string, string | undefined>)[key]
  if (!raw) return fallback
  const value = Number.parseFloat(raw)
  if (!Number.isFinite(value) || value < 0 || value > 1) {
    console.warn(`[sentry] invalid ${key}=${raw}, using default ${fallback}`)
    return fallback
  }
  return value
}

export function initSentry(): void {
  state.init_attempted = true

  const env = import.meta.env as Record<string, string | undefined>
  const dsn = (env.VITE_SENTRY_DSN ?? '').trim()
  if (!dsn) {
    console.info('[sentry] VITE_SENTRY_DSN not set, skipping (no-op)')
    return
  }
  state.dsn_configured = true

  void (async () => {
    try {
      const Sentry = await import('@sentry/react')

      const environment =
        env.VITE_SENTRY_ENVIRONMENT ||
        (import.meta.env.PROD ? 'production' : 'development')
      const release = (env.VITE_RELEASE_SHA ?? '').slice(0, 7) || 'unknown'
      const tracesSampleRate = readFloatEnv('VITE_SENTRY_TRACES_SAMPLE_RATE', 0.0)
      const replaysSampleRate = readFloatEnv('VITE_SENTRY_REPLAYS_SAMPLE_RATE', 0.0)

      Sentry.init({
        dsn,
        environment,
        release,
        tracesSampleRate,
        replaysSessionSampleRate: replaysSampleRate,
        replaysOnErrorSampleRate: 0.0,
        sendDefaultPii: false,
        integrations: [],
      })

      state.enabled = true
      console.info(
        `[sentry] initialized env=${environment} release=${release} traces=${tracesSampleRate}`,
      )
    } catch (err) {
      state.init_error = err instanceof Error ? `${err.name}: ${err.message}` : String(err)
      console.error('[sentry] init failed (app continues without Sentry):', err)
    }
  })()
}

export function isSentryEnabled(): boolean {
  return state.enabled
}

export function sentryStatus(): Readonly<SentryState> {
  return { ...state }
}
